import React, { useRef, useState, useEffect } from 'react';
import Hls from 'hls.js';
import { startLiveCatchupMonitor, schedulePlayLiveSync, HLS_LIVE_CONFIG } from '../lib/livePlaybackCatchup';
import { canPlayNativeHls, shouldPreferNativeHls } from '../lib/nativeHls';
import { attachSessionPlayback, isSessionActive } from '../lib/sessionActivity';

// Componente che riceve l'URL completo, ma con logica HLS identica a VideoPlayerHls
const VideoPlayerHlsSingoloFlusso = ({ videoStreaming }) => {
  const videoRef = useRef(null);
  // const [videoSrc, setVideoSrc] = useState(videoStreaming); // Non serve più stato interno per src
  const [reloadTrigger, setReloadTrigger] = useState(false); // Usa booleano come in VideoPlayerHls
  const [isPictureInPictureActive, setIsPictureInPictureActive] = useState(false);

  // Rimosso useEffect per impostare videoSrc, usiamo direttamente videoStreaming

  //console.log(`[SingoloFlusso] URL VIDEO da prop: ${videoStreaming}`);

  useEffect(() => {
    let hls = null;
    let reloadTimeoutId = null;
    let stopLiveCatchup = null;

    const playVideo = () => {
      if (!isSessionActive()) {
        return;
      }
      if (videoRef.current) {
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.then(() => {
            if (isPictureInPictureActive && document.pictureInPictureElement !== videoRef.current) {
              videoRef.current.requestPictureInPicture().catch(error => {
                console.error('[SingoloFlusso] Errore riattivazione PiP:', error);
              });
            }
          }).catch(error => {
             if (error && error.name === 'NotAllowedError' && videoRef.current && !videoRef.current.muted) {
                videoRef.current.muted = true;
                videoRef.current.play().catch(() => {});
                return;
             }
             if (error.name !== 'AbortError') {
                console.error(`[SingoloFlusso] Errore play per: ${videoStreaming}`, error);
             }
          });
        }
      }
    };

    const handlePlay = () => {
      schedulePlayLiveSync(videoRef.current, () => hls);
    };

    const handleVisibilityChange = () => {
      if (!isSessionActive() || document.hidden || !videoRef.current) {
        return;
      }
      if (videoRef.current.paused) {
        playVideo();
      }
      schedulePlayLiveSync(videoRef.current, () => hls);
    };

    const initializeHls = () => {
      if (!videoStreaming || typeof videoStreaming !== 'string' || !videoStreaming.includes('.m3u8')) {
        console.warn('[SingoloFlusso] URL prop non valido o non fornito:', videoStreaming);
        return;
      }

      const video = videoRef.current;
      const useNativeHls = shouldPreferNativeHls(video) || (!Hls.isSupported() && canPlayNativeHls(video));

      if (useNativeHls) {
          video.src = videoStreaming;
          video.playsInline = true;
          const currentListener = video.__playVideoListener;
          if (currentListener) {
              video.removeEventListener('loadedmetadata', currentListener);
          }
          const newListener = () => { playVideo(); };
          video.addEventListener('loadedmetadata', newListener);
          video.__playVideoListener = newListener;
      } else if (Hls.isSupported()) {
        hls = new Hls({
          ...HLS_LIVE_CONFIG,
        });
        hls.loadSource(videoStreaming);
        hls.attachMedia(videoRef.current);

        hls.on(Hls.Events.ERROR, function (event, data) {
            console.error(`[SingoloFlusso] Errore HLS (${data.type}):`, data.details, videoStreaming);
          if (data.fatal) {
             console.error('[SingoloFlusso] Errore fatale HLS');
            if (!isSessionActive()) {
              return;
            }
            if (data.type !== Hls.ErrorTypes.NETWORK_ERROR || data.details !== Hls.ErrorDetails.MANIFEST_PARSING_ERROR) {
                console.warn('[SingoloFlusso] Tento reload dopo errore fatale...');
                if (reloadTimeoutId) {
                    clearTimeout(reloadTimeoutId);
                }
                reloadTimeoutId = setTimeout(() => {
                    if (!isSessionActive()) {
                      return;
                    }
                    setReloadTrigger(prevState => !prevState)
                }, 5000);
            }
          }
        });

        hls.on(Hls.Events.MANIFEST_PARSED, function () {
          if (videoRef.current) {
            const currentListener = videoRef.current.__playVideoListener;
            if (currentListener) {
                videoRef.current.removeEventListener('loadedmetadata', currentListener);
            }
            const newListener = () => { playVideo(); };
            videoRef.current.addEventListener('loadedmetadata', newListener);
            videoRef.current.__playVideoListener = newListener;
          }
        });

      } else if (videoRef.current && videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
          videoRef.current.src = videoStreaming;
          const currentListener = videoRef.current.__playVideoListener;
          if (currentListener) {
              videoRef.current.removeEventListener('loadedmetadata', currentListener);
          }
          const newListener = () => { playVideo(); };
          videoRef.current.addEventListener('loadedmetadata', newListener);
          videoRef.current.__playVideoListener = newListener;

      } else {
        console.error('[SingoloFlusso] Questo browser non supporta HLS');
      }
    };

    const handleLeavePictureInPicture = () => {
      setIsPictureInPictureActive(false);
      setTimeout(playVideo, 200);
    };

    const handleEnterPictureInPicture = () => {
      setIsPictureInPictureActive(true);
    };

    initializeHls();

    const detachSessionPlayback = attachSessionPlayback(videoRef.current, () => hls);
    const videoElement = videoRef.current;
    if (videoElement) {
        videoElement.addEventListener('leavepictureinpicture', handleLeavePictureInPicture);
        videoElement.addEventListener('enterpictureinpicture', handleEnterPictureInPicture);
        videoElement.addEventListener('play', handlePlay);
        stopLiveCatchup = startLiveCatchupMonitor(videoElement, () => hls);
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      detachSessionPlayback();

      if (stopLiveCatchup) {
        stopLiveCatchup();
        stopLiveCatchup = null;
      }

      if (reloadTimeoutId) {
        clearTimeout(reloadTimeoutId);
      }

      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (videoElement) {
        videoElement.removeEventListener('leavepictureinpicture', handleLeavePictureInPicture);
        videoElement.removeEventListener('enterpictureinpicture', handleEnterPictureInPicture);
        videoElement.removeEventListener('play', handlePlay);
        const listener = videoElement.__playVideoListener;
        if (listener) {
             videoElement.removeEventListener('loadedmetadata', listener);
        }
        videoElement.pause();
        videoElement.removeAttribute('src');
        videoElement.load();
      }
      if (hls) {
        hls.destroy();
      }
    };
  }, [videoStreaming, reloadTrigger, isPictureInPictureActive]); 

  return (
    <video
      autoPlay // Come in VideoPlayerHls
      // muted // Rimosso, come in VideoPlayerHls
      playsInline // Mantenuto per iOS
      controls // Come in VideoPlayerHls
      ref={videoRef}
      className="custom-video-player-pc" // Come in VideoPlayerHls
      key={reloadTrigger} // Come in VideoPlayerHls
      style={{ backgroundColor: '#000' }} // Mantenuto per stile base
      // crossOrigin="anonymous" // Rimosso, non presente in VideoPlayerHls
    ></video>
  );
};

export default VideoPlayerHlsSingoloFlusso;